import { useCallback } from 'react';
import isNil from 'lodash/isNil';
import { SPRINT_STATES } from '@atlassian/jira-shared-types/src/rest/jira/sprint.tsx';
import type { MenuNode } from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import type { Column } from '../../../../../../../../model/column/column-types.tsx';
import { getSwimlaneId } from '../../../../../../../../services/issue/issue-data-transformer.tsx';
import { issueRankTeamDateRequest } from '../../../../../../../../state/actions/issue/rank-team-date/index.tsx';
import { useBoardSelector, useBoardDispatch } from '../../../../../../../../state/index.tsx';
import { cardSingleSelect, cardFocus } from '../../../../../../../../state/actions/card/index.tsx';
import { orderedColumnsSelector } from '../../../../../../../../state/selectors/column/column-selectors.tsx';
import { boardIssuesSelector } from '../../../../../../../../state/selectors/issue/board-issue-selectors.tsx';
import { getSwimlaneMode } from '../../../../../../../../state/selectors/swimlane/swimlane-mode-selectors.tsx';
import { getSwimlanes } from '../../../../../../../../state/selectors/swimlane/swimlane-selectors.tsx';
import { getTeamSprintsForIterations } from '../../../../../../../../state/selectors/team/team-selectors.tsx';
import type { State } from '../../../../../../../../state/types.tsx';
import type { Props } from './types.tsx';
import { useChangeDateRangeContent } from './view.tsx';

export const useChangeDateRange = ({
	issueId,
	selectedCardIds,
	isMultiSelectMode,
}: Props): MenuNode[] => {
	const allIssues = useBoardSelector(boardIssuesSelector);
	const triggerIssue = allIssues[String(issueId)];
	const columns = useBoardSelector(orderedColumnsSelector);
	const dispatch = useBoardDispatch();

	const swimlaneMode = useBoardSelector(getSwimlaneMode);
	const swimlaneId = useBoardSelector((state: State) =>
		!isNil(triggerIssue) ? getSwimlaneId(swimlaneMode, getSwimlanes(state), triggerIssue) : null,
	);

	const teamSprintsForIterations = useBoardSelector((state) =>
		getTeamSprintsForIterations(state, swimlaneId),
	);

	let filteredColumns: Column[] = isMultiSelectMode
		? columns
		: columns.filter((column: Column) => column.id !== triggerIssue.columnId);

	if (teamSprintsForIterations !== null) {
		/* if there are sprints associated with columns, we need an extra layer of filtering
            to remove closed sprints from the available options */
		filteredColumns = filteredColumns.filter(
			(column: Column) =>
				teamSprintsForIterations[Number(column.id)]?.state !== SPRINT_STATES.CLOSED,
		);
		/* TODO JPO-26040: Also need to filter the 'unscheduled' column from the available options
            in the case where an issue has only completed sprints assigned.
            (We don't yet have this data available on the issue) */
	}

	const onDateRangeChange = useCallback(
		(destinationColumn: Column) => {
			const moveCardsAction = issueRankTeamDateRequest({
				issueIds: selectedCardIds,
				sourceColumnId: triggerIssue.columnId,
				destinationColumnId: destinationColumn.id,
				rankBeforeIssueId: null,
				rankAfterIssueId: null,
				sourceSwimlaneId: swimlaneId,
				destinationSwimlaneId: swimlaneId,
			});
			dispatch(moveCardsAction);
			dispatch(cardSingleSelect(issueId));
			dispatch(cardFocus(issueId));
		},
		[dispatch, issueId, selectedCardIds, swimlaneId, triggerIssue.columnId],
	);

	return useChangeDateRangeContent({
		onDateRangeChange,
		columns: filteredColumns,
		teamSprintsForIterations,
	});
};
